import { Grid } from "@material-ui/core";
import {
  Send,
  X,
  Smile,
  Loader,
  Mic,
  Square,
  Plus,
  Paperclip,
  Check,
  Users,
} from "react-feather";
import styled from "styled-components/macro";
import TextareaAutosize from "react-textarea-autosize";
import { useRef, useState, useEffect, useMemo } from "react";
import EmojiPicker from "emoji-picker-react";
import styles from "../../communication-chat.module.css";

const ChatInputsTwo = styled(Grid)`
  min-height: 40px;
  padding: 16px;
  position: relative;
`;

const CompactChatInputSec = styled.div`
  background: #ffffff !important;
  border: 1px solid #e1e1e1 !important;
  border-radius: 8px !important;
  width: 100% !important;
  display: flex !important;
  align-items: flex-end !important;
  padding: 8px !important;
  gap: 6px !important;

  textarea {
    font-weight: 400 !important;
    font-size: 14px !important;
    line-height: 22px !important;
    color: #343434 !important;
    padding: 9px 10px 7px 10px !important;
    width: 100% !important;
    border: none !important;
    display: flex !important;
    align-items: center !important;
    resize: none !important;
    height: 39px !important;
    outline: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  textarea::-webkit-scrollbar {
    width: 5px !important;
  }
  textarea::-webkit-scrollbar-track {
    background: transparent !important;
  }
  textarea::-webkit-scrollbar-thumb {
    background: #ced0d1 !important;
  }
  textarea::-webkit-scrollbar-thumb:hover {
    background: #ced0d1 !important;
  }
`;

const EmojiPickerWrapper = styled.div`
  position: absolute;
  bottom: 52px;
  left: 40px;
  z-index: 10;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;

  /* emoji-picker-react sets its own defaults directly on .EmojiPickerReact,
     so overriding these variables on a parent div is ignored — they have
     to be redeclared on that same class, with !important, to actually win. */
  .EmojiPickerReact {
    --epr-emoji-size: 20px !important;
    --epr-header-padding: 8px !important;
    --epr-search-input-height: 32px !important;
    --epr-category-navigation-button-size: 22px !important;
    --epr-category-label-height: 24px !important;
    --epr-preview-height: 0px !important;
    --epr-emoji-gap: 2px !important;
    --epr-search-input-text-size: 12px !important;
    --epr-category-label-text-size: 11px !important;
  }

  /* Fall back to the underlying class names directly — some versions of
     emoji-picker-react don't wire every text size to a CSS variable. */
  .epr-search-container input {
    font-size: 13px !important;
  }
  .epr-emoji-category-label {
    font-size: 13px !important;
    font-weight: 500 !important;
    padding: 4px 8px !important;
  }
`;

const ActionButton = styled.button`
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  min-height: 32px !important;
  background: transparent !important;
  border: none !important;
  border-radius: 50% !important;
  display: flex !important;
  color: #ff8271 !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  cursor: pointer !important;
  transition: background-color 0.2s ease-in-out !important;

  &:hover {
    background-color: rgba(255, 130, 113, 0.1) !important;
  }

  svg {
    width: 18px !important;
    height: 18px !important;
  }
`;

const SendButton = styled.button`
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  min-height: 32px !important;
  background: #ff8271 !important;
  border: none !important;
  border-radius: 50% !important;
  display: flex !important;
  color: #fff !important;
  align-items: center !important;
  justify-content: center !important;
  transition: all 0.2s ease-in-out !important;
  cursor: pointer !important;

  &:hover {
    background: #e97565 !important;
  }

  svg {
    width: 16px;
    height: 16px;
    margin-left: 2px !important;
  }
`;

const MAX_RECORDING_SECONDS = 300; // 5 minutes

const IdentityChatInputBox = ({
  messageTextVal,
  handleChange,
  sendMessage,
  onSelectFile,
  onSelectRecordedAudio,
  isUploading,
  editingMessage,
  onCancelEdit,
  replyingToMessage,
  onCancelReply,
  resolveAuthorName,
  currentUserId,
  mentionableUsers,
  isGroupChat,
}) => {
  const isEditing = !!editingMessage;
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(null);
  const [pendingMentions, setPendingMentions] = useState([]); // [{ id, name }]
  const canMention = mentionableUsers?.length > 0;

  // When editing, start from the mentions already on the message
  useEffect(() => {
    if (editingMessage) {
      const existingIds = editingMessage._message?.attributes?.mentions || [];
      const seeded = existingIds
        .map((id) => {
          const name = id === "all" ? "all" : resolveAuthorName?.(id);
          return name ? { id, name } : null;
        })
        .filter(Boolean);
      setPendingMentions(seeded);
    } else {
      setPendingMentions([]);
    }
    // resolveAuthorName is recreated on every parent render; re-running on it
    // would wipe mentions picked during the edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingMessage]);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const emojiButtonRef = useRef(null);
  const emojiPopoverRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const streamRef = useRef(null);
  const sendRecordedAudioRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isEmojiOpen &&
        emojiPopoverRef.current &&
        !emojiPopoverRef.current.contains(e.target) &&
        !emojiButtonRef.current?.contains(e.target)
      ) {
        setIsEmojiOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEmojiOpen]);

  // Clean up mic stream and timer if the component unmounts mid-recording
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleEmojiClick = (emojiData) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      handleChange({ target: { value: messageTextVal + emojiData.emoji } });
      return;
    }
    const start = textarea.selectionStart ?? messageTextVal.length;
    const end = textarea.selectionEnd ?? messageTextVal.length;
    const newValue =
      messageTextVal.slice(0, start) +
      emojiData.emoji +
      messageTextVal.slice(end);
    handleChange({ target: { value: newValue } });
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + emojiData.emoji.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      onSelectRecordedAudio(
        null,
        "Voice recording isn't supported in this browser."
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      recordedChunksRef.current = [];

      // Prefer audio/mp4 (AAC): it plays on Android, iOS and desktop. iOS has
      // no WebM/Opus decoder, so a webm note shows as unplayable for iPhone
      // users of the management app. Fall back to webm only if the browser
      // can't record mp4 (e.g. Firefox) — same order as the management app.
      const mimeType = MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        const file = new File(
          [blob],
          `voice-note-${Date.now()}.${
            mimeType.includes("webm") ? "webm" : "m4a"
          }`,
          {
            type: mimeType,
          }
        );
        // Through the ref: this callback was created when recording started,
        // and the send must use the latest reply/text state
        sendRecordedAudioRef.current(file);
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      recorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => {
          if (prev + 1 >= MAX_RECORDING_SECONDS) {
            stopRecording();
            return prev + 1;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      console.log("getUserMedia failed", err);
      onSelectRecordedAudio(
        null,
        "Microphone access was denied. Please allow microphone access to record a voice note."
      );
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
  };

  const formatSeconds = (total) => {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // "@que|" with no whitespace between the @ and the cursor → { atIndex, query }
  const detectMentionTrigger = (text, cursorPos) => {
    const uptoCursor = text.slice(0, cursorPos);
    const atIndex = uptoCursor.lastIndexOf("@");
    if (atIndex === -1) return null;
    const between = uptoCursor.slice(atIndex + 1);
    if (/\s/.test(between)) return null;
    return { atIndex, query: between };
  };

  const handleTextChange = (e) => {
    handleChange(e);

    if (!canMention) {
      setShowMentionPicker(false);
      return;
    }
    const value = e.target.value || "";
    const cursorPos = e.target.selectionStart ?? value.length;
    const trigger = detectMentionTrigger(value, cursorPos);
    if (trigger) {
      setMentionQuery(trigger.query);
      setMentionStartIndex(trigger.atIndex);
      setShowMentionPicker(true);
    } else {
      setShowMentionPicker(false);
    }
  };

  const filteredMentionOptions = useMemo(() => {
    const query = mentionQuery.toLowerCase();
    const users = (mentionableUsers || [])
      .filter((u) =>
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(query)
      )
      .sort((a, b) =>
        `${a.first_name} ${a.last_name}`.localeCompare(
          `${b.first_name} ${b.last_name}`
        )
      );
    const options = [];
    if (isGroupChat && "all".includes(query)) {
      options.push({ id: "all", name: "all", isAll: true });
    }
    options.push(
      ...users.map((u) => ({
        id: u.id,
        name: `${u.first_name} ${u.last_name}`,
        avatar: u.avatar || null,
        firstName: u.first_name,
        lastName: u.last_name,
      }))
    );
    return options;
  }, [mentionQuery, mentionableUsers, isGroupChat]);

  const handleSelectMention = (option) => {
    const currentText = messageTextVal || "";
    const before = currentText.slice(0, mentionStartIndex);
    const after = currentText.slice(mentionStartIndex + 1 + mentionQuery.length);
    const insertName = option.isAll ? "all" : option.name;
    handleChange({ target: { value: `${before}@${insertName} ${after}` } });
    setPendingMentions((prev) => [...prev, { id: option.id, name: insertName }]);
    setShowMentionPicker(false);
    setMentionQuery("");

    setTimeout(() => {
      const textarea = textareaRef.current;
      if (textarea) {
        const pos = before.length + insertName.length + 2;
        textarea.focus();
        textarea.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  // Only send mentions whose @Name is still in the text
  const getActiveMentions = () =>
    pendingMentions.filter((m) =>
      (messageTextVal || "").includes(`@${m.name}`)
    );

  const handleSend = () => {
    sendMessage(getActiveMentions());
    setPendingMentions([]);
    setShowMentionPicker(false);
  };

  // Files are sent as soon as they're picked (no preview), with any typed
  // text as the caption — same as the management app
  const handleFileSelected = (file) => {
    onSelectFile(file, getActiveMentions());
    setPendingMentions([]);
  };

  sendRecordedAudioRef.current = (file) => {
    onSelectRecordedAudio(file, null, getActiveMentions());
    setPendingMentions([]);
  };

  const getComposerReplyLabel = (msg) => {
    if (msg.message) return msg.message.slice(0, 120);
    const mediaType = msg.media?.[0]?.content_type || "";
    if (mediaType.startsWith("image/")) return "📷 Photo";
    if (mediaType.startsWith("video/")) return "🎥 Video";
    if (mediaType.startsWith("audio/")) return "🎤 Voice message";
    if (mediaType) return "📎 Attachment";
    return "";
  };

  return (
    <div>
      {isEditing && (
        <div className={styles.WhatsappComposerReplyBar}>
          <div className={styles.WhatsappComposerReplyInfo}>
            <p className={styles.WhatsappComposerReplySender}>
              Editing message
            </p>
          </div>
          <button
            onClick={onCancelEdit}
            type="button"
            className={styles.WhatsappComposerReplyCloseBtn}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {replyingToMessage && !isEditing && (
        <div className={styles.WhatsappComposerReplyBar}>
          <div className={styles.WhatsappComposerReplyInfo}>
            <p className={styles.WhatsappComposerReplySender}>
              Replying to{" "}
              {String(replyingToMessage.author) === String(currentUserId)
                ? "You"
                : resolveAuthorName?.(replyingToMessage.author)}
            </p>
            <p className={styles.WhatsappComposerReplyText}>
              {getComposerReplyLabel(replyingToMessage)}
            </p>
          </div>
          <button
            onClick={onCancelReply}
            type="button"
            className={styles.WhatsappComposerReplyCloseBtn}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <ChatInputsTwo>
        {showMentionPicker && filteredMentionOptions.length > 0 && (
          <div className={styles.WhatsappMentionPickerBox}>
            {filteredMentionOptions.map((opt) => (
              <div
                key={opt.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelectMention(opt)}
                className={styles.WhatsappMentionPickerItem}
              >
                <div
                  className={`${styles.WhatsappMentionAvatar} ${opt.isAll ? styles.AllAvatar : ""
                    }`}
                >
                  {opt.isAll ? (
                    <Users size={14} color="#502688" />
                  ) : opt.avatar ? (
                    <img
                      src={opt.avatar}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <p className={styles.WhatsappMentionInitials}>
                      {`${opt.firstName?.charAt(0) || ""}${opt.lastName?.charAt(0) || ""
                        }`.toUpperCase()}
                    </p>
                  )}
                </div>
                <div className={styles.WhatsappMentionTextGroup}>
                  <p
                    className={`${styles.WhatsappMentionTitle} ${opt.isAll ? styles.AllTitle : ""
                      }`}
                  >
                    {opt.name}
                  </p>
                  {opt.isAll && (
                    <span className={styles.WhatsappMentionSubtext}>
                      Mention all members
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {isEmojiOpen && (
          <EmojiPickerWrapper ref={emojiPopoverRef}>
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              height={320}
              width={350}
              previewConfig={{ showPreview: false }}
              searchDisabled={false}
            />
          </EmojiPickerWrapper>
        )}

        {isRecording ? (
          <CompactChatInputSec style={{ justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  background: "#e23725",
                }}
              />
              <span style={{ fontSize: "14px", color: "#343434" }}>
                Recording… {formatSeconds(recordingSeconds)}
              </span>
            </div>
            <SendButton
              onClick={stopRecording}
              style={{ background: "#e23725", borderColor: "#e23725" }}
              type="button"
            >
              <Square size={18} />
            </SendButton>
          </CompactChatInputSec>
        ) : (
          <CompactChatInputSec>
            {!isEditing && (
              <ActionButton
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                style={{ opacity: isUploading ? 0.5 : 1 }}
                type="button"
              >
                <Paperclip />
              </ActionButton>
            )}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files?.[0]) handleFileSelected(e.target.files[0]);
                e.target.value = "";
              }}
            />

            <ActionButton
              ref={emojiButtonRef}
              onClick={() => setIsEmojiOpen((prev) => !prev)}
              type="button"
            >
              <Smile />
            </ActionButton>

            <Grid item style={{ flexGrow: 1 }}>
              <TextareaAutosize
                ref={textareaRef}
                minRows={1}
                maxRows={3}
                placeholder="Type your message"
                value={messageTextVal}
                onChange={handleTextChange}
                onBlur={() => setShowMentionPicker(false)}
              />
            </Grid>

            {isEditing ? (
              <SendButton
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSend}
                disabled={!(messageTextVal || "").trim()}
                style={{
                  opacity: (messageTextVal || "").trim() ? 1 : 0.6,
                }}
                type="button"
              >
                <Check />
              </SendButton>
            ) : !(messageTextVal || "").trim() && !isUploading ? (
              <ActionButton onClick={startRecording} type="button">
                <Mic />
              </ActionButton>
            ) : (
              <SendButton
                onClick={handleSend}
                disabled={isUploading}
                style={{ opacity: isUploading ? 0.6 : 1 }}
                type="button"
              >
                {isUploading ? <Loader size={20} className={`${styles.UploadSpinIcon}`} /> : <Send />}
              </SendButton>
            )}
          </CompactChatInputSec>
        )}
      </ChatInputsTwo>
    </div>
  );
};

export default IdentityChatInputBox;